import {PayloadAction} from "@reduxjs/toolkit";
import axios, {AxiosResponse} from "axios";
import {showMessage, SNACKBAR_MESSAGE_SEVERITY_ERROR} from "../../hocs/snackbar-provider";
import {getManagedErrorsTranslations} from "../../translations/getManagedErrorsTranslations";
import {errorRequest, REQUEST_INIT, RequestActionPayload, startRequest, successRequest} from "./requestActions";
import {CATALOG_FETCH} from "../../state/catalog/catalogActions";
import {DATASET_STRUCTURE_FETCH} from "../../state/dataset/datasetActions";
import {
  DATASET_MVP_DOWNLOAD_SUBMIT,
  DATASET_MVP_MAP_DATASET_FETCH,
  DATASET_MVP_STRUCTURE_CODELIST_FETCH,
  DATASET_MVP_TABLE_CHART_DATASET_FETCH,
  DATASET_MVP_TERRITORY_TERRITORIES_FETCH
} from "../../state/dataset/multi-viewer-plus/actions";
import {
  DATASET_MV_DATASET_FETCH,
  DATASET_MV_DOWNLOAD_SUBMIT,
  DATASET_MV_STRUCTURE_CODELIST_FETCH,
  DATASET_MV_TERRITORY_TERRITORIES_FETCH
} from "../../state/dataset/multi-viewer/actions";
import {DATASET_SVP_CODELISTS_FETCH, DATASET_SVP_DATASET_FETCH} from "../../state/dataset/single-viewer-plus/actions";
import {
  DATASET_SV_DATASET_FETCH,
  DATASET_SV_DOWNLOAD_SUBMIT,
  DATASET_SV_STRUCTURE_CODELIST_FETCH
} from "../../state/dataset/single-viewer/actions";
import {setRedirectUrl} from "../../state/miscellaneous/miscellaneousActions";
import {addPendingRequest, cancelPendingRequests} from "../../state/pending-request/pendingRequestActions";
import {updateSpinnerMessageSize} from "../../state/spinner/spinnerActions";

const joinUrl = (base: string, path: string): string => {
  const trimmedBase = base?.endsWith("/") ? base.slice(0, -1) : base || "";
  const trimmedPath = path?.startsWith("/") ? path.slice(1) : path || "";

  if (!trimmedBase) return trimmedPath;
  if (!trimmedPath) return trimmedBase;

  return `${trimmedBase}/${trimmedPath}`;
};

const nodeLocalizedRequests: string[] = [
  /* commons */
  CATALOG_FETCH,
  DATASET_STRUCTURE_FETCH,
  /* SingleViewer */
  DATASET_SV_STRUCTURE_CODELIST_FETCH,
  DATASET_SV_DATASET_FETCH,
  DATASET_SV_DOWNLOAD_SUBMIT,
  /* SingleViewerPlus */
  DATASET_SVP_CODELISTS_FETCH,
  DATASET_SVP_DATASET_FETCH,
  DATASET_SVP_DATASET_FETCH,
  /* MultiViewer */
  DATASET_MV_STRUCTURE_CODELIST_FETCH,
  DATASET_MV_TERRITORY_TERRITORIES_FETCH,
  DATASET_MV_DATASET_FETCH,
  DATASET_MV_DOWNLOAD_SUBMIT,
  /* MultiViewerPlus */
  DATASET_MVP_STRUCTURE_CODELIST_FETCH,
  DATASET_MVP_TERRITORY_TERRITORIES_FETCH,
  DATASET_MVP_TABLE_CHART_DATASET_FETCH,
  DATASET_MVP_MAP_DATASET_FETCH,
  DATASET_MVP_DOWNLOAD_SUBMIT
];

const requestMiddlewareFactory =
  t =>
  ({dispatch, getState}) =>
  next =>
  (action: PayloadAction<any>) => {
    const handleSuccess = (requestAction: RequestActionPayload, response: AxiosResponse) => {
      dispatch(
        successRequest(requestAction, response.data, {
          responseHeaders: response.headers,
          status: response.status
        })
      );
    };

    const handleManagedServerError = (requestAction: RequestActionPayload, response: AxiosResponse) => {
      if (!requestAction?.getHideErrorMessage || !requestAction.getHideErrorMessage(response?.status)) {
        const translation = getManagedErrorsTranslations(t, response.data?.data)[response.data?.errorCode];
        const message = translation
          ? translation
          : response.data?.showMessage && response.data?.message
            ? response.data.message
            : t("errors.generic");

        showMessage(message, SNACKBAR_MESSAGE_SEVERITY_ERROR);
      }

      dispatch(errorRequest(requestAction, response.status, response.data, {responseHeaders: response.headers}));
    };

    const handleGenericError = (requestAction: RequestActionPayload, response?: AxiosResponse) => {
      if (!requestAction?.getHideErrorMessage || !requestAction.getHideErrorMessage(response?.status)) {
        showMessage(t("errors.generic"), SNACKBAR_MESSAGE_SEVERITY_ERROR);
      }

      dispatch(errorRequest(requestAction, response?.status, response?.data, {responseHeaders: response?.headers}));
    };

    const handleError = (requestAction: RequestActionPayload, response?: AxiosResponse) => {
      if (response?.data) {
        if (response.data.errorCode !== undefined) {
          handleManagedServerError(requestAction, response);
        } else {
          handleGenericError(requestAction, response);
        }
      } else {
        handleGenericError(requestAction, response);
      }
    };

    const result = next(action);

    if (action.type !== REQUEST_INIT) {
      return result;
    }

    const appLang = getState().app.language;

    const getRequestLanguage = (label: string) => {
      const node = getState().node;

      if (!node || !nodeLocalizedRequests.includes(label)) {
        return appLang;
      }

      const nodeExtras = getState().node?.extras || [];
      const nodeSupportedLanguages = JSON.parse(
        nodeExtras.find((extra: any) => extra.key === "SupportedLanguages")?.value || "[]"
      );
      const nodeDefaultLanguage = nodeExtras.find((extra: any) => extra.key === "DefaultLanguage")?.value || null;

      return nodeSupportedLanguages.length === 0 || !nodeDefaultLanguage || nodeSupportedLanguages.includes(appLang)
        ? appLang
        : nodeDefaultLanguage;
    };

    const requestAction: PayloadAction<RequestActionPayload> = action;

    dispatch(startRequest(requestAction.payload));

    const {
      uuid,
      label,
      method,
      url,
      data,
      baseURL,
      contentType,
      doNotTransformResponse,
      disableCache,
      showResponseSize,
      additionalHeaders
    } = requestAction.payload;

    const token = getState().user.token;

    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    dispatch(addPendingRequest(uuid, label, source));

    axios
      .request({
        method,
        url: joinUrl(baseURL && baseURL.length > 0 ? baseURL : getState().config.baseURL, url),
        data,
        headers: {
          UserLang: getRequestLanguage(label),
          Authorization: token ? `bearer ${token}` : undefined,
          "Content-Type": contentType || undefined,
          "Cache-Control": disableCache ? "no-cache" : undefined,
          Pragma: disableCache ? "no-cache" : undefined,
          Expires: disableCache ? "0" : undefined,
          ...(additionalHeaders ?? {})
        },
        withCredentials: true,
        transformResponse: doNotTransformResponse ? res => res : undefined,
        responseType: doNotTransformResponse ? "arraybuffer" : undefined,
        onDownloadProgress: progressEvent => {
          if (showResponseSize && progressEvent.loaded) {
            dispatch(updateSpinnerMessageSize(uuid, progressEvent.loaded));
          }
        },
        cancelToken: source.token
      })
      .then((response: AxiosResponse) => {
        dispatch(cancelPendingRequests([uuid]));

        if (response.status.toString()[0] === "2") {
          handleSuccess(requestAction.payload, response);
        } else {
          handleError(requestAction.payload, response);
        }
      })
      .catch(error => {
        if (!axios.isCancel(error)) {
          if ((error?.response?.status || "").toString() === "403" && error?.response?.data?.Reason) {
            dispatch(setRedirectUrl(`/${appLang}/service-unavailable`));
          } else {
            console.log(error);
          }
          handleError(requestAction.payload, error.response);
        }
      });

    return result;
  };

export default requestMiddlewareFactory;
